import React from 'react'
// libs
import { useMutation } from '@apollo/client/react'
// hooks
import { useShowModal } from 'hooks/useShowModal'
// components
import Grid from '@mui/material/Grid2'
import Tree from 'components/Tree'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// context
import { PositionsAPIContext } from 'containers/Positions/layers/API'
// constants
import { Modals } from 'constants/modals'
// graphql
import { POSITION_DELETE_MUTATION, POSITIONS_TREE_UPDATE_MUTATION } from 'graphql/position/mutations'
// themes
import { ActiveLink } from 'themes/views/tree'
import { REACT_SORTABLE_TREE_STYLES } from 'themes'
// utils
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

const Placeholder = ({ handleAddChild }) => {
  return (
    <Grid container justifyContent={'center'} alignItems={'center'} direction={'column'}>
      <Grid>There is no position here.</Grid>
      <Grid>
        Click <ActiveLink onClick={() => handleAddChild({})}>here</ActiveLink> to create your first position
      </Grid>
    </Grid>
  )
}

const PositionsTree = () => {
  const [deleteMutation] = useMutation(POSITION_DELETE_MUTATION)
  const [treeUpdateMutation] = useMutation(POSITIONS_TREE_UPDATE_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.BUSINESS_ENTITY_MODAL })
  const { showModal: showConfirm } = useShowModal({ modalType: Modals.CONFIRM_DELETE_MODAL })

  const { isLoading, positions, refetch } = React.useContext(PositionsAPIContext)

  const synchronizeTree = async (tree) => {
    try {
      await treeUpdateMutation({
        variables: {
          positions: tree.map(({ id, parentId, position, expanded }) => ({ id, parentId, position, expanded }))
        }
      })
    } catch (errors) {
      console.log(errors)
    }
  }

  const handleDelete = ({ node }) =>
    showConfirm({
      width: 'sm',
      handleConfirm: async () => {
        try {
          await deleteMutation({
            variables: {
              id: node.id
            }
          })

          await refetch()
        } catch (errors) {
          console.log('[PositionsTree.handleDelete]: ', parseGraphQLErrorMessages(errors))
        }
      }
    })

  const handleEdit = ({ node }) =>
    showModal({
      width: 'sm',
      item: node
    })

  const handleAddChild = ({ node }) =>
    showModal({
      width: 'sm',
      parent: node
    })

  const generateActionHandlers = () => ({
    actionsHandlers: {
      handleEditClick: ({ node }) => handleEdit({ node }),
      handleDeleteClick: ({ node }) => handleDelete({ node }),
      handleAddChildClick: ({ node }) => handleAddChild({ node })
    }
  })

  return (
    <>
      <Tree
        refetch={refetch}
        source={positions}
        isLoading={isLoading}
        synchronizeTree={synchronizeTree}
        generateActionHandlers={generateActionHandlers}
        placeholder={<Placeholder handleAddChild={handleAddChild} />}
        reactVirtualizedListProps={{
          style: REACT_SORTABLE_TREE_STYLES
        }}
      />
      <ConfirmDeleteModal header={'Delete Position'}>Are you sure you want to delete this position?</ConfirmDeleteModal>
    </>
  )
}

export default PositionsTree
